import type { ReactNode } from 'react'
import {
  Search,
  AlertCircle,
  CarFront,
  Phone,
  User,
  Wrench,
  CalendarClock,
  ShieldCheck,
  Radio,
  PowerOff,
} from 'lucide-react'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { Switch } from '../../lib/shadcn/switch'
import { Label } from '../../lib/shadcn/label'
import { Checkbox } from '../../lib/shadcn/checkbox'
import type { RowSelection } from '../../hooks/useRowSelection'
import { EditVehicleDialog } from './EditVehicleDialog'
import { IncidentFormDialog } from './IncidentFormDialog'
import { VehicleDocumentsDialog } from './VehicleDocumentsDialog'
import { Chip } from '../interviews/Chip'
import { cn } from '../../lib/shadcn/utils'
import {
  type FleetRow,
  type StatusTone,
  fleetStatusLabel,
  fleetStatusTone,
  holdReasonChips,
  locationLabel,
  locationTone,
  formatDate,
} from '../../utils/fleet'

function locationChip(row: FleetRow): { tone: StatusTone; label: string } {
  if (row.operational === false) {
    const loc = row.location || 'Unknown'
    return { tone: 'red', label: `Off-road · ${loc}` }
  }
  return { tone: locationTone(row.location), label: locationLabel(row.location) }
}

function incidentSummary(row: FleetRow): string | null {
  const parts = [row.last_incident_type, row.last_incident_outcome].filter(Boolean)
  if (parts.length === 0 && !row.last_incident_at) return null
  const when = row.last_incident_at ? formatDate(row.last_incident_at) : null
  if (when) parts.push(when)
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * What the car has actually done, in three numbers.
 *
 * Luyanda asked for days offline and availability on 1 September 2026, then for
 * availability on active cars too and a third number for every car: all offline
 * days since onboarding.
 *
 *   DAYS OFFLINE   the current run, days since the car last completed a shift.
 *                  Printed only when the car is not active. On an active car the
 *                  answer is "it has not been standing", and a 1 beside every row
 *                  turns a number that should make somebody act into wallpaper.
 *
 *   AVAILABILITY   days worked over days since the first shift. On every car,
 *                  because an active car can still be the worst asset in the
 *                  fleet: MR66MXGP is active today and has worked 199 of 477 days.
 *
 *   TOTAL OFFLINE  every day since the first shift on which the car did not work.
 *                  On every car. Rest days count: four drivers rotate so that the
 *                  CAR runs every day, and a day the asset did not earn is a day
 *                  the asset did not earn.
 *
 * SINCE THE FIRST SHIFT, NOT SINCE ONBOARDING, and the caption says so. There is
 * no onboarding date on file for any vehicle: activated_at is NULL on all 81 rows
 * and created_at reads 2026-07-25 on all 81, the day the table was loaded. The
 * first shift is the only evidenced start, so the label names it rather than
 * letting a number answer a question it cannot.
 *
 * All three come from v_vehicle_uptime. A car with no shift on record shows a
 * dash, never a zero.
 */
function Stat({
  value,
  label,
  sub,
  tone,
}: {
  value: string
  label: string
  sub?: string
  tone?: string
}) {
  return (
    <div className="min-w-[78px]">
      <div className={cn('text-2xl font-bold leading-none tabular-nums', tone ?? 'text-foreground')}>
        {value}
      </div>
      <div className="mt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {sub && <div className="text-[10px] leading-tight text-muted-foreground">{sub}</div>}
    </div>
  )
}

function availabilityTone(pct: number | null): string {
  if (pct == null) return 'text-muted-foreground'
  if (pct >= 80) return 'text-emerald-700 dark:text-emerald-300'
  if (pct >= 50) return 'text-amber-700 dark:text-amber-300'
  return 'text-rose-700 dark:text-rose-300'
}

function UptimeStrip({ row }: { row: FleetRow }) {
  const days = row.days_offline
  const avail = row.availability_pct
  const total = row.offline_days_total
  const showRun = row.is_active !== true
  const known = row.platform_days != null

  return (
    <div className="mt-2 flex flex-wrap items-stretch gap-4 rounded-md border border-border bg-muted/30 px-3 py-2">
      {showRun && (
        <>
          <Stat
            value={days == null ? '—' : String(days)}
            label={days === 1 ? 'Day offline' : 'Days offline'}
            sub="current run"
          />
          <div className="w-px bg-border" />
        </>
      )}
      <Stat
        value={avail == null ? '—' : `${avail}%`}
        label="Availability"
        sub={known ? `${row.active_days} of ${row.platform_days} days worked` : undefined}
        tone={availabilityTone(avail)}
      />
      <div className="w-px bg-border" />
      <Stat
        value={total == null ? '—' : String(total)}
        label="Total offline"
        sub={
          row.first_shift_date ? `since first shift ${formatDate(row.first_shift_date)}` : undefined
        }
      />
      {!known && (
        <div className="flex items-end pb-0.5 text-[10px] leading-tight text-muted-foreground">
          No shift on record for this vehicle.
        </div>
      )}
    </div>
  )
}

function Fact({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
      {icon}
      {children}
    </span>
  )
}

export function FleetList({
  vehicles,
  loading,
  error,
  search,
  onSearchChange,
  activeOnly,
  onActiveOnlyChange,
  canEditFleet,
  onReload,
  selection,
}: {
  vehicles: FleetRow[]
  loading: boolean
  error: string | null
  search: string
  onSearchChange: (v: string) => void
  activeOnly: boolean
  onActiveOnlyChange: (v: boolean) => void
  canEditFleet: boolean
  onReload: () => void
  selection?: RowSelection<FleetRow>
}) {
  // Checkboxes only render for editors with an active selection controller;
  // viewers never see them.
  const showCheckboxes = canEditFleet && selection != null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by registration, owner, make or model"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
            <Switch
              id="active-only"
              checked={activeOnly}
              onCheckedChange={onActiveOnlyChange}
            />
            <Label htmlFor="active-only" className="cursor-pointer text-xs font-normal">
              Active only
            </Label>
          </div>
          <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
            {loading ? '…' : `${vehicles.length} result${vehicles.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {showCheckboxes && selection && vehicles.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="fleet-select-all"
              checked={selection.allSelected ? true : selection.someSelected ? 'indeterminate' : false}
              onCheckedChange={() => selection.toggleAll()}
              aria-label="Select all vehicles"
            />
            <Label htmlFor="fleet-select-all" className="cursor-pointer text-xs font-normal">
              Select all {vehicles.length}
            </Label>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : loading && vehicles.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading fleet…
          </div>
        ) : vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <CarFront className="h-6 w-6" />
            No vehicles match your filters.
          </div>
        ) : (
          <ul>
            {vehicles.map((v) => {
              const loc = locationChip(v)
              const descriptor = [v.make, v.model].filter(Boolean).join(' ')
              const yearColour = [v.year ? `(${v.year})` : null, v.colour]
                .filter(Boolean)
                .join(' ')
              const incident = incidentSummary(v)
              const holds = holdReasonChips(v.hold_reasons)
              return (
                <li key={v.vehicle_id}>
                  <div className="flex flex-col gap-2 border-b border-border px-3 py-3 sm:flex-row sm:items-start">
                    {showCheckboxes && selection && (
                      <Checkbox
                        className="mt-0.5 shrink-0"
                        checked={selection.isSelected(v.vehicle_id)}
                        onCheckedChange={() => selection.toggle(v.vehicle_id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${v.registration_number || 'vehicle'}`}
                      />
                    )}
                    {/* Left: identity */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-sm font-bold uppercase tracking-wide text-foreground">
                          {v.registration_number || '—'}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {descriptor || 'Unknown vehicle'}
                          {yearColour ? ` · ${yearColour}` : ''}
                        </span>
                      </div>

                      {/* Owner / driver line */}
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {v.owner_name && (
                          <Fact icon={<User className="h-3 w-3" />}>
                            {v.owner_name}
                            {v.owner_phone ? ` · ${v.owner_phone}` : ''}
                          </Fact>
                        )}
                        {v.current_driver && (
                          <Fact icon={<Phone className="h-3 w-3" />}>
                            Driver: {v.current_driver}
                          </Fact>
                        )}
                      </div>

                      {/* Muted facts */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {v.telematics_status && (
                          <Fact icon={<Radio className="h-3 w-3" />}>{v.telematics_status}</Fact>
                        )}
                        {v.compliance_status && (
                          <Fact icon={<ShieldCheck className="h-3 w-3" />}>
                            {v.compliance_status}
                          </Fact>
                        )}
                        {v.next_service_date && (
                          <Fact icon={<CalendarClock className="h-3 w-3" />}>
                            Service {formatDate(v.next_service_date)}
                          </Fact>
                        )}
                        {v.repairs_due && (
                          <Fact icon={<Wrench className="h-3 w-3" />}>{v.repairs_due}</Fact>
                        )}
                        {incident && (
                          <Fact icon={<AlertCircle className="h-3 w-3" />}>{incident}</Fact>
                        )}
                        {v.is_active === false && (v.inactive_reasons ?? []).length > 0 && (
                          <Fact icon={<PowerOff className="h-3 w-3" />}>
                            Inactive: {(v.inactive_reasons ?? []).join('; ')}
                          </Fact>
                        )}
                      </div>

                      {/* Every onboarded car, and any other car that has shift
                          history worth reading. An offboarded car's availability
                          is the beginning of an answer to why it left. */}
                      {(v.lifecycle_status === 'active' || v.platform_days != null) && (
                        <UptimeStrip row={v} />
                      )}
                    </div>

                    {/* Right: status chips + edit affordances */}
                    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:flex-col sm:items-end">
                      {/* Active first, because it is the answer. The lifecycle
                          stage below it says how far the car got, not whether it
                          is earning today. */}
                      <Chip tone={v.is_active ? 'green' : 'red'}>
                        {v.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                      <Chip tone={fleetStatusTone(v.lifecycle_status)}>
                        {fleetStatusLabel(v.lifecycle_status)}
                      </Chip>
                      <Chip tone={loc.tone}>{loc.label}</Chip>
                      {holds.map((h) => (
                        <Chip key={h.label} tone={h.tone}>
                          {h.label}
                        </Chip>
                      ))}
                      {v.in_general_fleet === false && (
                        <Chip tone="neutral">Not general fleet</Chip>
                      )}
                      <div className="flex flex-wrap items-center gap-2 pt-1 sm:justify-end">
                        <VehicleDocumentsDialog vehicle={v} />
                        {canEditFleet && (
                          <>
                            <EditVehicleDialog vehicle={v} onSaved={onReload} />
                            <IncidentFormDialog vehicle={v} onSaved={onReload} />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
